// StudentMeets.jsx — Redesigned: compact, responsive, content-dense
import { useState, useEffect, Fragment } from "react";
import { Helmet } from "react-helmet-async";
import { getMeeting, registerStudent } from "../services/api";
import "./StudentMeets.css";

/* ───── SVG Icons ───── */
const I = {
  back: (
    <svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
      <path d="M9 2 4 7l5 5" />
    </svg>
  ),
  arrow: (
    <svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <path d="M2 7h10M8 3l4 4-4 4" />
    </svg>
  ),
  clock: (
    <svg width="12" height="12" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="7" cy="7" r="5.5" />
      <path d="M7 4v3l2 1.5" />
    </svg>
  ),
  external: (
    <svg width="13" height="13" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
      <path d="M6 2H3a1 1 0 0 0-1 1v8a1 1 0 0 0 1 1h8a1 1 0 0 0 1-1V8" />
      <path d="M8 2h4v4M12 2 7 7" />
    </svg>
  ),
  check: (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M4 12.5 10 18.5 20 7" />
    </svg>
  ),
  live: (
    <svg width="8" height="8" viewBox="0 0 8 8" fill="currentColor">
      <circle cx="4" cy="4" r="4" />
    </svg>
  ),
};

/* ───── Countdown ───── */
function Countdown({ sessionTime }) {
  const [timeLeft, setTimeLeft] = useState(null);
  const [isLive, setIsLive] = useState(false);

  useEffect(() => {
    const calc = () => {
      try {
        const [t, mod] = sessionTime.split(" ");
        let [h, m] = t.split(":").map(Number);
        if (mod === "PM" && h !== 12) h += 12;
        if (mod === "AM" && h === 12) h = 0;
        const now = new Date(), target = new Date();
        target.setHours(h, m, 0, 0);
        if (target < now) target.setDate(target.getDate() + 1);
        const diff = Math.max(0, Math.floor((target - now) / 1000));
        if (diff < 3600 && now.getHours() === h) { setIsLive(true); return; }
        setIsLive(false);
        setTimeLeft({ h: Math.floor(diff / 3600), m: Math.floor((diff % 3600) / 60), s: diff % 60 });
      } catch { setTimeLeft(null); }
    };
    calc();
    const id = setInterval(calc, 1000);
    return () => clearInterval(id);
  }, [sessionTime]);

  if (isLive) {
    return (
      <div className="sm-live">
        {I.live}
        <span>Session is live now</span>
      </div>
    );
  }

  if (!timeLeft) return null;

  const pad = (n) => String(n).padStart(2, "0");
  const units = [["Hours", timeLeft.h], ["Minutes", timeLeft.m], ["Seconds", timeLeft.s]];

  return (
    <div className="sm-countdown">
      {units.map(([lbl, val], i) => (
        <Fragment key={lbl}>
          {i > 0 && <span className="sm-countdown-sep">:</span>}
          <div className="sm-countdown-unit">
            <span className="sm-countdown-num">{pad(val)}</span>
            <span className="sm-countdown-lbl">{lbl}</span>
          </div>
        </Fragment>
      ))}
    </div>
  );
}

const COURSES = [
  "MBBS Abroad", "MS / Masters Abroad", "Undergraduate (UG)", "MBA Abroad",
  "Study in UK", "Study in Canada", "Study in Australia", "Study in Germany",
  "Study in USA", "Immigration / PR", "Other",
];

const INIT_FORM = { name: "", phone: "", email: "", city: "", course_interest: "" };

const FIELDS = [
  { name: "name", label: "Full name", type: "text", placeholder: "e.g. Priya Sharma" },
  { name: "phone", label: "Phone", type: "tel", placeholder: "10-digit number", maxLength: 10, inputMode: "numeric" },
  { name: "email", label: "Email", type: "email", placeholder: "you@example.com" },
  { name: "city", label: "City", type: "text", placeholder: "e.g. Hyderabad" },
];

/* ───── Component ───── */
export default function StudentMeets() {
  const [meeting, setMeeting] = useState(null);
  const [meetLoading, setMeetLoading] = useState(true);
  const [meetError, setMeetError] = useState("");
  const [form, setForm] = useState(INIT_FORM);
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [registered, setRegistered] = useState(false);
  const [submitErr, setSubmitErr] = useState("");

  useEffect(() => {
    getMeeting()
      .then((r) => setMeeting(r.data))
      .catch(() => setMeetError("No active session right now. Please check back later."))
      .finally(() => setMeetLoading(false));
  }, []);

  const validate = () => {
    const e = {};
    if (!form.name.trim()) e.name = "Name is required";
    if (!/^[6-9]\d{9}$/.test(form.phone)) e.phone = "Valid 10-digit number required";
    if (!/\S+@\S+\.\S+/.test(form.email)) e.email = "Valid email required";
    if (!form.city.trim()) e.city = "City is required";
    if (!form.course_interest) e.course_interest = "Please select a course";
    return e;
  };

  const handleChange = (e) => {
    setForm((f) => ({ ...f, [e.target.name]: e.target.value }));
    setErrors((err) => ({ ...err, [e.target.name]: "" }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const errs = validate();
    if (Object.keys(errs).length) { setErrors(errs); return; }
    setSubmitting(true);
    setSubmitErr("");
    try {
      await registerStudent(form);
      setRegistered(true);
      setForm(INIT_FORM);
    } catch (err) {
      setSubmitErr(err.response?.data?.detail || "Registration failed. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="sm-root">
      <Helmet>
        <title>Student Meets | ISTP Overseas — Meet Abroad University Reps</title>
        <meta name="description" content="Join ISTP Overseas student meet events in Vijayawada. Interact with university representatives, alumni and counsellors from top study abroad destinations." />
        <link rel="canonical" href="https://www.istpoverseas.com/student-meets" />
        <meta property="og:title" content="Student Meets | ISTP Overseas" />
        <meta property="og:description" content="Meet university reps and alumni at ISTP Overseas student events in Vijayawada." />
        <meta property="og:url" content="https://www.istpoverseas.com/student-meets" />
      </Helmet>
      <main className="sm-main">
        {/* Intro */}
        <section className="sm-intro">
          <span className="sm-eyebrow">
            <span className="sm-dot" />
            Daily live session
          </span>
          <h1 className="sm-title">
            Student <em>Meet</em>
          </h1>
          <p className="sm-sub">
            Join our counsellors live for your study-abroad questions — answered in real time.
          </p>
        </section>

        {/* Layout: session card + form */}
        <div className="sm-layout">
          {/* Session card */}
          <aside className="sm-session">
            {meetLoading ? (
              <div className="sm-spinner" />
            ) : meetError ? (
              <div className="sm-session-err">{meetError}</div>
            ) : (
              <>
                <div className="sm-session-head">
                  <div>
                    <span className="sm-session-eyebrow">Next session</span>
                    <h2 className="sm-session-title">{meeting.title}</h2>
                  </div>
                  <span className="sm-session-time">
                    {I.clock} {meeting.session_time}
                  </span>
                </div>

                <div className="sm-session-body">
                  <Countdown sessionTime={meeting.session_time} />
                </div>

                {meeting.is_active && (
                  <a
                    href={meeting.meet_link}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="sm-btn sm-btn-primary"
                  >
                    Join live session {I.external}
                  </a>
                )}
              </>
            )}
          </aside>

          {/* Form */}
          <section className="sm-form-panel">
            {registered ? (
              <div className="sm-success">
                <div className="sm-success-badge">{I.check}</div>
                <h2 className="sm-success-title">You're registered</h2>
                <p className="sm-success-sub">
                  We'll send the meeting link to your email and phone before the session.
                </p>
                <button
                  className="sm-btn sm-btn-primary"
                  onClick={() => setRegistered(false)}
                >
                  Register another {I.arrow}
                </button>
              </div>
            ) : (
              <>
                <div className="sm-form-head">
                  <div>
                    <h2 className="sm-form-title">Register for free</h2>
                    <p className="sm-form-sub">We'll notify you before the session starts.</p>
                  </div>
                  <span className="sm-badge-free">Free</span>
                </div>

                {submitErr && <div className="sm-alert">{submitErr}</div>}

                <form onSubmit={handleSubmit} noValidate className="sm-form">
                  <div className="sm-form-grid">
                    {FIELDS.map((f) => (
                      <div className="sm-field" key={f.name}>
                        <label htmlFor={`sm-${f.name}`}>{f.label}</label>
                        <input
                          id={`sm-${f.name}`}
                          name={f.name}
                          type={f.type}
                          placeholder={f.placeholder}
                          maxLength={f.maxLength}
                          inputMode={f.inputMode}
                          value={form[f.name]}
                          onChange={handleChange}
                          autoComplete="off"
                        />
                        {errors[f.name] && <span className="sm-field-err">{errors[f.name]}</span>}
                      </div>
                    ))}
                  </div>

                  <div className="sm-field">
                    <label htmlFor="sm-course">Course interest</label>
                    <select
                      id="sm-course"
                      name="course_interest"
                      value={form.course_interest}
                      onChange={handleChange}
                    >
                      <option value="">Select an area</option>
                      {COURSES.map((c) => <option key={c} value={c}>{c}</option>)}
                    </select>
                    {errors.course_interest && (
                      <span className="sm-field-err">{errors.course_interest}</span>
                    )}
                  </div>

                  <button
                    type="submit"
                    className="sm-btn sm-btn-primary sm-submit"
                    disabled={submitting}
                  >
                    {submitting ? "Registering…" : <>Register for free {I.arrow}</>}
                  </button>
                </form>

                <div className="sm-trust">
                  <span>Free, always</span>
                  <span className="sm-trust-dot" />
                  <span>Link by email + phone</span>
                  <span className="sm-trust-dot" />
                  <span>Expert counsellors</span>
                </div>
              </>
            )}
          </section>
        </div>
      </main>

      <footer className="sm-footer">
        <span>© 2026 ISTP Overseas Education.</span>
        <span>All rights reserved.</span>
      </footer>
    </div>
  );
}